import * as React from 'react';
import toTree from 'src/internal/compiler/toTree';
import {NavigationConfigContext} from 'src/internal/config/NavigationConfigContext';
import HrefContext from 'src/internal/contexts/HrefContext';
import NavigationUiProvider from 'src/internal/providers/NavigationUiProvider';
import RouteConfigsContext from 'src/public/Router/RouteConfigsContext';
import useRouteConfigs from 'src/public/Router/useRouteConfigs';

import type {
  FullNavigationConfig,
  HrefHook,
  NavigationConfig,
  NavigationConfigInternal,
} from 'src/types';

type Props = {
  /** The app content that uses navigation. */
  children: React.ReactNode;
  /** Configuration object containing routes, href state, and optional processing functions. */
  config: NavigationConfig;
};

function mergeNavConfigs(
  defaults: NavigationConfigInternal | undefined,
  config: FullNavigationConfig,
): FullNavigationConfig {
  return {
    ...defaults,
    ...config,
  };
}

function isFullNavigationConfig(
  config: NavigationConfig,
): config is FullNavigationConfig {
  return 'routes' in config;
}

function NavigationConfigProvider({
  children,
  config,
}: {
  children: React.ReactNode;
  config: FullNavigationConfig;
}): React.ReactElement {
  const currentNavigationInternalConfig = React.useContext(
    NavigationConfigContext,
  );
  const {href, setHref, routes, unsafePreProcessRoute, postProcessUrl} =
    mergeNavConfigs(currentNavigationInternalConfig, config);

  const hrefValue = React.useMemo<HrefHook>(
    () => [href, setHref],
    [href, setHref],
  );

  const navigationConfigInternal = React.useMemo(
    () => ({routes, postProcessUrl, unsafePreProcessRoute}),
    [routes, postProcessUrl, unsafePreProcessRoute],
  );

  const parentRouteConfigs = useRouteConfigs();

  toTree(config.routes);

  return (
    <NavigationConfigContext.Provider value={navigationConfigInternal}>
      <HrefContext.Provider value={hrefValue}>
        <NavigationUiProvider>
          <RouteConfigsContext.Provider
            value={config.__do_not_use_routeConfigs ?? parentRouteConfigs}
          >
            {children}
          </RouteConfigsContext.Provider>
        </NavigationUiProvider>
      </HrefContext.Provider>
    </NavigationConfigContext.Provider>
  );
}

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-navigation/api/NavigationProvider
 */
export default function NavigationProvider({
  children,
  config,
}: Props): React.ReactElement {
  if (isFullNavigationConfig(config)) {
    return (
      <NavigationConfigProvider config={config}>
        {children}
      </NavigationConfigProvider>
    );
  }

  return (
    <RouteConfigsContext.Provider value={config.__do_not_use_routeConfigs}>
      {children}
    </RouteConfigsContext.Provider>
  );
}
